/**
 * Cart Tiers Module
 * Manages cart reward tiers: free gift thresholds, progress bar, tier item add/remove.
 *
 * Uses theme.cartTiers adapter for cart operations (falls back to Shopify AJAX defaults).
 * Listens for theme events via adapter.
 *
 * State machine for handleCartChange:
 *   idle → running → draining (if events queued) → idle
 * This replaces separate _skipNextBuild / _reconciling / _handling flags.
 */

function logger(message) {
    window.theme?.customElementDebug && console.log("CRS", message);
}

// Theme adapter — falls back to Shopify AJAX if no theme adapter provided
const api = window.theme?.cartTiers?.api || {
    addItem:     (id) => fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) }).then(r => r.json()),
    removeItem:  (key) => fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) }).then(r => r.json()),
    changeItem:  (key, qty) => fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) }).then(r => r.json()),
    getCart:      () => fetch('/cart.js').then(r => r.json()),
    formatMoney:  (cents) => `$${(cents / 100).toFixed(2)}`
};

const events = window.theme?.cartTiers?.events || {
    cartUpdated: 'cart:updated',
    cartBuild: 'cart:build',
    productAdded: 'ajaxProduct:added'
};

// ─── State machine ──────────────────────────────────────────────────
// Replaces the previous _skipNextBuild / _reconciling / _handling flags.
// "idle"     – nothing running, next handleCartChange runs immediately
// "running"  – reconcile + saleChecks in progress; cart:build listener
//              skips its getCart (caller handles progress update)
// "draining" – same as running, but a newer cart is queued for re-run
let _state = 'idle';
let _pendingCart = null;    // stashed cart for the drain pass
let tierQueue = Promise.resolve();

function enqueueTierReconcile(cart) {
    tierQueue = tierQueue.then(() => reconcileTiers(cart)).catch(err => {
        console.error("CRS reconcileTiers error:", err);
    });
    return tierQueue;
}

// ─── Sale pricing (pure logic) ──────────────────────────────────────

function getActiveDiscount(cart) {
    const s = theme.settings;

    if (s.saleEventShowSaleByTag === true) {
        const tagDiscount = s.saleByTagDiscount;
        return { multiplier: 1 - tagDiscount / 100, pitch: `${tagDiscount}% off` };
    }
    if (s.saleEventShowBFCMSale === true) {
        const thresholdPrice = (s.bfcmThresholdCents || 8500) * window.Shopify.currency.rate;
        const deep = cart.original_total_price >= thresholdPrice;
        return {
            multiplier: deep ? (s.bfcmDeepMultiplier || 0.65) : (s.bfcmBaseMultiplier || 0.75),
            pitch: deep ? `${Math.round((1 - (s.bfcmDeepMultiplier || 0.65)) * 100)}% off`
                        : `${Math.round((1 - (s.bfcmBaseMultiplier || 0.75)) * 100)}% off`
        };
    }
    if (s.saleEventShowValentinesDaySale === true) {
        const hasQtyOver2 = cart.items.some(item => item.quantity > 2);
        return {
            multiplier: hasQtyOver2 ? 0.8 : 0.85,
            pitch: hasQtyOver2 ? "Buy 2 items, get 20% off" : "15% Off Everything"
        };
    }
    return null;
}

function updateSalePricingDom(discount) {
    if (!discount) return;
    const { multiplier, pitch } = discount;
    logger(["Discount of", multiplier]);

    document.querySelectorAll(".pdp-pricing").forEach(container => {
        const price = container.getAttribute("price");
        const newPrice = Math.ceil(price * multiplier);
        const amountSaved = price - newPrice;

        const salePriceEl = container.querySelector(".pdp-sale-price");
        if (salePriceEl) {
            if (container.getAttribute("has-deep-discount") === "false") {
                salePriceEl.innerText = api.formatMoney(newPrice);
            }
            salePriceEl.classList.remove("element-hidden");
            container.querySelector(".pdp-original-price")?.classList.add("grid-product__price--original");
        }

        const pitchEl = container.querySelector(".pdp-sales-pitch");
        if (pitchEl) pitchEl.innerText = pitch;

        const savedEl = container.querySelector(".pdp-saved-amount");
        if (savedEl) savedEl.innerText = ` -${api.formatMoney(amountSaved)} `;
    });

    if (theme.settings.saleShowBanners === true) {
        document.querySelectorAll(".percent_off-container").forEach(container => {
            let percentTxt = Math.round((1 - multiplier) * 100);
            if (container.getAttribute("has-deep-discount") === "true") {
                percentTxt = Math.round((1 - parseFloat(container.getAttribute("discount"))) * 100);
            }
            const textEl = container.querySelector(".percent-text");
            if (textEl) textEl.innerText = `${percentTxt}%`;
            container.classList.remove("element-hidden");
        });
    }
}

async function saleChecks(cart) {
    logger(["saleChecks", cart]);
    if (theme.settings.saleShowCrossOutPrices === true) {
        const discount = getActiveDiscount(cart);
        updateSalePricingDom(discount);
    }
    await updateTierProgressDom(cart);
}

// ─── Free gift after X products ─────────────────────────────────────

async function reconcileFreeGift(cartItems, tiers) {
    if (!window.theme?.freeGiftVariantId || !window.theme?.freeGiftAfterX) return 0;

    const freeGiftAfterX = window.theme.freeGiftAfterX;
    const freeGiftVariantId = window.theme.freeGiftVariantId;
    const tierVariantIds = Object.keys(tiers).map(k => tiers[k].variant_id);
    const cartQtyWithoutTierItems = cartItems.reduce((acc, item) => {
        if (!(item.variant_id in tierVariantIds) && item.variant_id !== freeGiftVariantId) {
            acc += item.quantity;
        }
        return acc;
    }, 0);
    const freeItemInCart = cartItems.some(item => item.variant_id === freeGiftVariantId);
    const surpassedThreshold = cartQtyWithoutTierItems >= freeGiftAfterX;

    logger(["Free gift after X products?", { cartQtyWithoutTierItems, freeGiftAfterX, freeGiftVariantId, freeItemInCart, surpassedThreshold }]);

    if (!freeItemInCart && surpassedThreshold) {
        logger(`Free gift surpassed threshold of ${freeGiftAfterX}! Adding to cart`);
        await api.addItem(freeGiftVariantId);
        return 1;
    }
    if (freeItemInCart && !surpassedThreshold) {
        logger(`Free gift NOT surpassed threshold of ${freeGiftAfterX}! Removing from cart`);
        const match = cartItems.find(item => item.variant_id === freeGiftVariantId);
        await api.removeItem(match.key);
        return 1;
    }
    return 0;
}

// ─── Tier reconciliation ────────────────────────────────────────────

async function reconcileTiers(cart) {
    if (window.theme.tierItemJustRemoved) {
        logger(["reconcileTiers - skipped, tier item was manually removed"]);
        window.theme.tierItemJustRemoved = false;
        return;
    }
    logger(["reconcileTiers", cart]);

    if (window.theme.tieredProductsCurrencyAdjusted !== true) {
        convertTierAmountsToCurrency();
    }

    const tiers = window.theme.tieredProducts;
    const cartItems = cart.items.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        key: item.key,
        properties: item.properties
    }));
    let cartChanged = 0;

    window.theme.activeRewardTier = null;

    try {
        cartChanged += await reconcileFreeGift(cartItems, tiers);

        for (const tierKey in tiers) {
            const tier = tiers[tierKey];
            const tierAmount = parseFloat(tier.amount);

            if (tier.type === 'milestone') continue;

            // choose_product — set active tier flag, don't auto-add
            if (tier.type === 'choose_product') {
                if (cart.total_price >= tierAmount) {
                    const hasRewardInCart = cartItems.some(item =>
                        item.properties && item.properties._reward_tier === tierKey
                    );
                    if (!hasRewardInCart) {
                        window.theme.activeRewardProductTier = {
                            key: tierKey,
                            tag: tier.tag,
                            threshold: tierAmount
                        };
                    }
                } else {
                    const rewardItem = cartItems.find(item =>
                        item.properties && item.properties._reward_tier === tierKey
                    );
                    if (rewardItem) {
                        cartChanged++;
                        await api.removeItem(rewardItem.key);
                    }
                }
                continue;
            }

            // Product-id tier (variant selection — customer picks size)
            if (tier.product_id) {
                const match = cartItems.find(item => item.product_id === tier.product_id);

                if (cart.total_price >= tierAmount) {
                    logger(["threshold met - product_id tier", tierAmount, tier.product_id]);
                    if (!match) {
                        if (!window.theme.activeRewardTier || tierKey > window.theme.activeRewardTier.key) {
                            window.theme.activeRewardTier = {
                                key: tierKey,
                                product_id: tier.product_id,
                                threshold: tierAmount,
                                useProductId: true
                            };
                        }
                    } else if (match.quantity > 1) {
                        logger(["    - too many qty, adjusting to 1", tier.product_id]);
                        cartChanged++;
                        await api.changeItem(match.key, 1);
                    }
                } else if (match) {
                    logger(["    - removing product_id tier from cart"]);
                    cartChanged++;
                    await api.removeItem(match.key);
                }
                continue;
            }

            // Variant-id tier (auto-add)
            const match = cartItems.find(item => item.variant_id === tier.variant_id);
            if (cart.total_price >= tierAmount) {
                if (!match || match.quantity < 1) {
                    logger(["threshold met, adding", tierAmount, tier.variant_id]);
                    cartChanged++;
                    await api.addItem(tier.variant_id);
                }
                if (match && match.quantity > 1) {
                    logger(["too many qty, adjusting to 1", tier.variant_id]);
                    cartChanged++;
                    await api.changeItem(match.key, 1);
                }
            } else if (match) {
                logger(["threshold NOT met, removing", tier.variant_id]);
                cartChanged++;
                await api.removeItem(match.key);
            }
        }
    } catch (e) {
        console.error("CRS reconcileTiers mutation failed:", e);
    }

    logger("reconcileTiers:done");
    if (cartChanged > 0) {
        document.dispatchEvent(new CustomEvent(events.cartBuild));
    }
    updateRewardTierSelector();
}

// ─── Reward variant selector ────────────────────────────────────────

function updateRewardTierSelector() {
    const container = document.getElementById("reward-tier-selector-container");
    if (!container) return;

    if (window.theme.activeRewardTier) {
        logger(["Showing reward tier selector", window.theme.activeRewardTier]);
        const selectorComponent = container.querySelector("reward-variant-selector");
        if (selectorComponent) {
            if (selectorComponent.dataset.hasError === "true") {
                logger(["Selector has error state, not showing"]);
                container.style.display = "none";
                return;
            }
            if (selectorComponent.dataset.loading === "false") {
                logger(["Product already loaded, resetting button states"]);
                selectorComponent.resetButtons && selectorComponent.resetButtons();
            } else {
                logger(["Loading product into selector by ID", window.theme.activeRewardTier.product_id]);
                selectorComponent.loadProduct(window.theme.activeRewardTier.product_id);
            }
        }
        container.style.display = "block";
    } else {
        logger(["Hiding reward tier selector"]);
        container.style.display = "none";
    }
}

// ─── Currency conversion ────────────────────────────────────────────

function convertTierAmountsToCurrency() {
    if (!Shopify?.currency?.rate) return false;

    const rate = parseFloat(Shopify.currency.rate);
    logger(['tieredProducts - USD', window.theme.tieredProducts]);

    if (rate === 1) {
        logger('Currency is USD, skipping currency conversion');
        window.theme.tieredProductsCurrencyAdjusted = true;
        return true;
    }

    const tiers = window.theme.tieredProducts;
    for (const key in tiers) {
        tiers[key].amount = (tiers[key].amount * rate).toFixed(2);
    }

    window.theme.tieredProductsCurrencyAdjusted = true;
    logger(['tieredProducts - currencyAdjusted', window.theme.tieredProducts]);
    return true;
}

// ─── Tier UI (DOM) ──────────────────────────────────────────────────

function buildTierUI() {
    const template = document.getElementById('cart-goal-template');
    if (!template) return;

    const containers = document.querySelectorAll('[data-cart-goal]');
    containers.forEach(container => {
        if (container.classList.contains('cart-goal-banner-container') && !window.theme.showTiersInBanner) return;

        const legend = container.querySelector('.cart-goal-legend');
        if (legend && legend.children.length === 0) {
            legend.appendChild(template.content.cloneNode(true));
        }
        container.style.display = '';
    });
}

// ─── localStorage cache ─────────────────────────────────────────────

const CACHE_KEY = 'cartTierState';

function applyCachedState() {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return;
        const state = JSON.parse(cached);

        document.querySelectorAll('.progress-bar').forEach(bar => {
            bar.style.transform = `scaleX(${(state.percent || 0) / 100})`;
            bar.classList.remove('hidden');
        });

        if (state.activeTiers) {
            state.activeTiers.forEach(tierKey => {
                document.querySelectorAll(`li[free-tier="${tierKey}"]`).forEach(el => {
                    el.classList.add('activated');
                });
            });
        }

        if (state.goalHtml) {
            document.querySelectorAll('.next-reach').forEach(el => {
                el.innerHTML = state.goalHtml;
            });
        }

        if (state.allComplete) {
            document.querySelectorAll('.progress-bar').forEach(el => {
                el.classList.add('bg-success');
                el.classList.remove('bg-primary');
            });
            document.querySelectorAll('.next-reach').forEach(el => {
                el.classList.add('text-success');
                el.classList.remove('text-primary');
            });
        }
    } catch (e) {
        // Ignore parse errors
    }
}

function saveTierState(percent, activeTiers, goalHtml, allComplete) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
            percent, activeTiers, goalHtml, allComplete, ts: Date.now()
        }));
    } catch (e) {
        // Ignore quota errors
    }
}

// ─── Progress bar DOM ───────────────────────────────────────────────

async function updateTierProgressDom(cart) {
    logger('updateTierProgressDom');
    if (window.theme.useTieredProducts !== true && window.theme.useTieredProducts !== "true") return;

    if (window.theme.tieredProductsCurrencyAdjusted !== true) {
        convertTierAmountsToCurrency();
    }
    const cartTotal = cart.total_price;

    logger(['cartTotal', cartTotal]);
    logger(['tieredProducts', window.theme.tieredProducts]);

    let allTiersComplete = true;
    let lastTierKey = 0;
    let nextTierKey = 0;
    const tiers = window.theme.tieredProducts;

    const activeTiers = Object.fromEntries(
        Object.entries(tiers).filter(([, tier]) =>
            (tier.variant_id && tier.variant_id !== 0) ||
            tier.product_id ||
            tier.type === 'milestone' ||
            tier.type === 'choose_product'
        )
    );

    const sortedKeys = Object.keys(activeTiers).sort((a, b) => parseFloat(activeTiers[a].amount) - parseFloat(activeTiers[b].amount));

    for (const key of sortedKeys) {
        if (parseFloat(activeTiers[key].amount) <= cartTotal) {
            lastTierKey = key;
        } else {
            nextTierKey = key;
            break;
        }
    }

    const activatedTierKeys = [];

    sortedKeys.forEach(key => {
        const isActivated = parseInt(key) <= parseInt(lastTierKey);
        document.querySelectorAll(`li[free-tier="${key}"]`).forEach(el => {
            el.classList.toggle('activated', isActivated);
        });
        if (isActivated) activatedTierKeys.push(key);
    });

    let percentCompleted = 0;
    const progressBars = document.querySelectorAll('.progress-bar');
    if (progressBars.length > 0) {
        const tierCount = sortedKeys.length;
        const legendEl = document.querySelector('.cart-goal-legend');
        let marginPct = 8;
        if (legendEl && legendEl.parentElement && legendEl.parentElement.offsetWidth > 0) {
            const ml = parseFloat(getComputedStyle(legendEl).marginLeft);
            if (!isNaN(ml)) marginPct = ml / legendEl.parentElement.offsetWidth * 100;
        }
        const tierPos = (idx) => marginPct + ((2 * idx + 1) / (2 * tierCount)) * (100 - 2 * marginPct);

        if (nextTierKey) {
            const nextIdx = sortedKeys.indexOf(nextTierKey);
            const lastIdx = lastTierKey ? sortedKeys.indexOf(lastTierKey) : -1;
            const lastAmount = lastTierKey ? parseFloat(activeTiers[lastTierKey].amount) : 0;
            const nextAmount = parseFloat(activeTiers[nextTierKey].amount);
            const progressInSegment = Math.min((cartTotal - lastAmount) / (nextAmount - lastAmount), 1);
            const startPos = lastIdx >= 0 ? tierPos(lastIdx) : 0;
            const endPos = tierPos(nextIdx);
            percentCompleted = startPos + progressInSegment * (endPos - startPos);
        } else {
            percentCompleted = 100;
        }

        percentCompleted = Math.min(Math.round(percentCompleted), 100);

        progressBars.forEach(bar => {
            bar.style.transform = `scaleX(${percentCompleted / 100})`;
            bar.classList.remove('hidden');
        });

        allTiersComplete = percentCompleted === 100;
    }

    const goalDescEls = document.querySelectorAll('.next-reach');
    let goalHtml = '';

    if (allTiersComplete) {
        document.querySelectorAll('.text-progress').forEach(el => {
            el.classList.add('text-success');
            el.classList.remove('text-primary');
        });
        document.querySelectorAll('.bg-progress').forEach(el => {
            el.classList.add('bg-success');
            el.classList.remove('bg-primary');
        });
        goalHtml = window.theme.tierSuccess;
        goalDescEls.forEach(el => {
            el.classList.add('text-success');
            el.innerHTML = goalHtml;
        });
    } else {
        document.querySelectorAll('.text-success').forEach(el => {
            el.classList.add('text-primary');
            el.classList.remove('text-success');
        });
        document.querySelectorAll('.bg-success').forEach(el => {
            el.classList.add('bg-primary');
            el.classList.remove('bg-success');
        });

        let goalDesc = activeTiers[nextTierKey].goal_description;
        goalHtml = goalDesc.replace('[amount]', api.formatMoney(parseFloat(activeTiers[nextTierKey].amount) - cartTotal));
        goalDescEls.forEach(el => {
            el.innerHTML = goalHtml;
        });
    }

    saveTierState(percentCompleted, activatedTierKeys, goalHtml, allTiersComplete);
}

// ─── Utility ────────────────────────────────────────────────────────

function isTierItem(id) {
    const tiers = Object.values(window.theme.tieredProducts || {});
    return tiers.some(t =>
        (t.variant_id && t.variant_id === id) ||
        (t.product_id && t.product_id === id)
    );
}

// ─── Custom elements ────────────────────────────────────────────────

class CartRemove extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
        const link = this.querySelector('a');
        if (!link) return;
        event.preventDefault();

        const cartItem = this.closest('.cart__item');
        if (cartItem) {
            const variantId = parseInt(cartItem.dataset.id);
            if (isTierItem(variantId)) {
                logger(["CartRemove: tier item removed, will skip next reconcileTiers"]);
                window.theme.tierItemJustRemoved = true;
            }
        }

        const key = cartItem?.dataset.key;
        if (key) {
            api.removeItem(key).then(() => {
                document.dispatchEvent(new CustomEvent(events.cartBuild));
                return api.getCart();
            }).then(cart => {
                document.dispatchEvent(new CustomEvent(events.cartUpdated, { detail: { cart } }));
            });
        }
    }
}

class RewardVariantSelector extends HTMLElement {
    constructor() {
        super();
        logger('RewardVariantSelector: Initializing');
        this.headerEl = this.querySelector('.reward-variant-selector__header');
        this.variantsContainer = this.querySelector('.reward-variant-selector__variants');
    }

    resetButtons() {
        logger('RewardVariantSelector: Resetting button states');
        this.querySelectorAll('.reward-variant-selector__button').forEach(btn => {
            btn.disabled = false;
            btn.classList.remove('adding', 'added');
            if (btn.dataset.variantTitle) btn.textContent = btn.dataset.variantTitle;
        });
    }

    async loadProduct(productId) {
        logger('RewardVariantSelector: Loading product', productId);

        const productHandle = window.theme.tierProductMap?.[productId];
        if (!productHandle) {
            console.error('RewardVariantSelector: Product handle not found for ID:', productId);
            this.dataset.hasError = 'true';
            window.theme.activeRewardTier = null;
            const container = document.getElementById('reward-tier-selector-container');
            if (container) container.style.display = 'none';
            return;
        }

        try {
            const response = await fetch(`/products/${productHandle}.js`);
            if (!response.ok) throw new Error(`Failed to fetch product: ${response.status}`);

            const product = await response.json();
            logger('RewardVariantSelector: Product loaded', product);
            this.renderProduct(product);
        } catch (error) {
            console.error('RewardVariantSelector: Error loading product:', error);
            this.dataset.hasError = 'true';
            window.theme.activeRewardTier = null;
            const container = document.getElementById('reward-tier-selector-container');
            if (container) container.style.display = 'none';
        }
    }

    renderProduct(product) {
        this.headerEl.innerHTML = `
            <h3 class="reward-variant-selector__title">Claim Your Free Gift!</h3>
            <p class="reward-variant-selector__subtitle">Choose your Size:</p>
        `;

        this.variantsContainer.innerHTML = '';

        const uniqueValues = [...new Set(product.variants.map(v => v.option1))];
        uniqueValues.forEach(optionValue => {
            const variant = product.variants.find(v => v.option1 === optionValue);
            if (variant) {
                const button = this.createVariantButton(variant, optionValue);
                this.variantsContainer.appendChild(button);
            }
        });

        this.dataset.loading = 'false';
    }

    createVariantButton(variant, label) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'reward-variant-selector__button';
        button.dataset.variantId = variant.id;
        button.dataset.variantTitle = label;

        if (variant.available) {
            button.textContent = label;
        } else {
            button.textContent = `${label} - Out of Stock`;
            button.disabled = true;
        }

        button.addEventListener('click', this.onVariantSelect.bind(this));
        return button;
    }

    async onVariantSelect(event) {
        const button = event.currentTarget;
        const variantId = button.dataset.variantId;

        logger('RewardVariantSelector: Variant selected:', variantId);

        if (!variantId || button.disabled) return;

        try {
            this.querySelectorAll('.reward-variant-selector__button').forEach(btn => btn.disabled = true);

            button.classList.add('adding');
            button.textContent = '…';

            logger('RewardVariantSelector: Adding variant to cart:', variantId);
            await api.addItem(parseInt(variantId));

            document.dispatchEvent(new CustomEvent(events.productAdded, {
                detail: { product: { id: variantId }, addToCartBtn: button },
                bubbles: true
            }));

            const container = document.getElementById('reward-tier-selector-container');
            if (container) container.style.display = 'none';
        } catch (error) {
            console.error('RewardVariantSelector: Error adding to cart:', error);

            this.querySelectorAll('.reward-variant-selector__button').forEach(btn => btn.disabled = false);

            button.classList.remove('adding');
            button.textContent = 'Error - Try Again';

            setTimeout(() => {
                button.textContent = button.dataset.variantTitle || 'Claim Free Gift';
            }, 2000);
        }
    }
}

// ─── Main handler (state machine) ───────────────────────────────────

window.theme.isTierItem = isTierItem;

async function runReconcileAndSaleChecks(cart) {
    await enqueueTierReconcile(cart);
    await saleChecks(cart);
}

async function handleCartChange(cart) {
    if (_state !== 'idle') {
        _pendingCart = cart;
        _state = 'draining';
        logger(["handleCartChange - queued (in-flight)"]);
        return;
    }
    _state = 'running';
    await runReconcileAndSaleChecks(cart);

    while (_pendingCart) {
        const next = _pendingCart;
        _pendingCart = null;
        _state = 'running';
        await runReconcileAndSaleChecks(next);
    }
    _state = 'idle';
}

// ─── Init & event listeners ─────────────────────────────────────────

function previewTierUI() {
    buildTierUI();

    const tiers = window.theme.tieredProducts;
    const sortedKeys = Object.keys(tiers).sort((a, b) => parseFloat(tiers[a].amount) - parseFloat(tiers[b].amount));
    if (!sortedKeys.length) return;

    // Activate the first tier
    document.querySelectorAll(`li[free-tier="${sortedKeys[0]}"]`).forEach(el => el.classList.add('activated'));

    // Set progress bar between first and second tier for a realistic preview
    const previewPercent = sortedKeys.length > 1
        ? Math.round(100 / sortedKeys.length * 1.4)
        : 100;

    // Defer so the browser lays out the now-visible containers first
    requestAnimationFrame(() => {
        document.querySelectorAll('.progress-bar').forEach(bar => {
            bar.style.transform = `scaleX(${Math.min(previewPercent, 100) / 100})`;
            bar.classList.remove('hidden');
        });
    });

    const nextTier = tiers[sortedKeys.length > 1 ? sortedKeys[1] : sortedKeys[0]];
    if (nextTier?.goal_description) {
        const previewHtml = nextTier.goal_description.replace('[amount]', '$XX.XX');
        document.querySelectorAll('.next-reach').forEach(el => { el.innerHTML = previewHtml; });
    }
}

function init() {
    if (window.theme.useTieredProducts !== true && window.theme.useTieredProducts !== "true") return;

    if (Shopify.designMode) {
        previewTierUI();
        // Re-render when section settings change in the editor
        document.addEventListener('shopify:section:load', previewTierUI);
        return;
    }

    buildTierUI();
    applyCachedState();

    // cart:updated — theme fires this with cart data after qty changes
    document.addEventListener(events.cartUpdated, async function(evt) {
        logger(["cart:updated", evt]);
        await handleCartChange(evt.detail.cart);
    });

    // cart:build — fired after tier mutations to trigger DOM rebuild.
    // When we're running (state !== idle), saleChecks already updates
    // the progress DOM, so skip the redundant getCart + update.
    // External dispatchers (e.g. custom-elements.js) still get the full update.
    document.addEventListener(events.cartBuild, async function() {
        if (_state !== 'idle') {
            logger(["cart:build - skipped (handled by caller)"]);
            return;
        }
        logger(["cart:build recalculation"]);
        try {
            const cart = await api.getCart();
            updateTierProgressDom(cart);
        } catch (e) {
            logger(["cart:build getCart failed (likely rate-limited)", e]);
        }
    });

    // ajaxProduct:added — product added via PDP form.
    // Skip when we're already running to avoid re-entry from our own dispatches.
    document.addEventListener(events.productAdded, async function(evt) {
        if (_state !== 'idle') {
            logger(["ajaxProduct:added - skipped (reconcile in progress)"]);
            return;
        }
        try {
            const cart = await api.getCart();
            logger(["ajaxProduct:added", evt, cart]);
            await handleCartChange(cart);
        } catch (e) {
            logger(["ajaxProduct:added getCart failed (likely rate-limited)", e]);
        }
    });

    // bundlr — bundle added to cart
    document.addEventListener("bndlr:bundle_added_to_cart", async function() {
        document.dispatchEvent(new CustomEvent("bundlr:productAdded", {
            bubbles: true,
            detail: {}
        }));
        try {
            const cart = await api.getCart();
            logger(["bndlr:bundle_added_to_cart", cart]);
            await handleCartChange(cart);
        } catch (e) {
            logger(["bndlr:bundle_added_to_cart getCart failed (likely rate-limited)", e]);
        }
    });

    // Wait for Shopify.currency.rate (set async by Shopify scripts), then run initial checks
    function waitForCurrencyRate(maxWait = 3000) {
        return new Promise(resolve => {
            if (Shopify?.currency?.rate) return resolve();
            const interval = 50;
            let elapsed = 0;
            const timer = setInterval(() => {
                elapsed += interval;
                if (Shopify?.currency?.rate || elapsed >= maxWait) {
                    clearInterval(timer);
                    resolve();
                }
            }, interval);
        });
    }

    window.addEventListener("load", async () => {
        await waitForCurrencyRate();
        try {
            const cart = await api.getCart();
            await handleCartChange(cart);
        } catch (e) {
            logger(["load getCart failed (likely rate-limited)", e]);
        }
    });

    // Register custom elements
    if (!customElements.get('cart-remove')) {
        customElements.define('cart-remove', CartRemove);
    }
    if (!customElements.get('reward-variant-selector')) {
        customElements.define('reward-variant-selector', RewardVariantSelector);
    }
}

init();
